import React from 'react';

import type { PwaInstallStep } from '../../components/PwaInstallSheet';

import {
  PwaInstallSheet,
  PwaInstallSteps,
} from '../../components/PwaInstallSheet';
import { Button } from '../../components/Button';
import createContextFactory from '../createContextFactory';
import { useIntl } from '../TranslationsProvider';
import { useIsMobileShell } from '../../hooks';
import {
  hidesShareBehindMore,
  isInAppBrowser,
  isInstalledApp,
  isIosDevice,
} from './installPlatform';
import messages from './messages';

export interface BeforeInstallPromptEvent extends Event {
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt: () => Promise<void>;
}

export interface PwaInstallPromptContextType {
  /**
   * Whether an "Install the app" entry may be offered now: on a phone, outside
   * the installed app and an app's own browser, to an account that may see the
   * install — and on Android only while Chrome holds an install offer.
   */
  canInstall: boolean;
  /**
   * Android: Chrome's install dialog, from the held offer. iPhone: the steps
   * sheet. Independent of the sheet's one automatic show per device.
   */
  openInstall: () => void;
}
const PwaInstallPromptContext =
  createContextFactory<PwaInstallPromptContextType>({
    devToolsName: 'PwaInstallPromptContext',
    // The anonymous public page mounts no provider: nothing to install there.
    initialValue: { canInstall: false, openInstall: () => undefined },
  });

// Chrome offers the install on every load of a not-installed app; the sheet
// answers that offer once per device. The iPhone has no offer to wait for, so
// its sheet is shown once per device too.
const ANDROID_PROMPT_SHOWN_KEY = 'mfwAndroidPwaPromptShown';
const IOS_PROMPT_SHOWN_KEY = 'mfwIosPwaPromptShown';
// `react-ios-pwa-prompt` showed the iPhone sheet until November 2026 and
// counted its shows under this key: such a device has had its one show —
// except on Safari 26, where its steps started at a Share button hidden behind
// "•••", so those devices get the corrected steps once more (owner, 04.11.2026).
const LEGACY_IOS_PROMPT_KEY = 'iosPwaPrompt';

function claimPromptShow(key: string, spentBy?: string) {
  try {
    if (localStorage.getItem(key)) return false;
    if (spentBy && localStorage.getItem(spentBy)) return false;
    localStorage.setItem(key, '1');
    return true;
  } catch {
    return false;
  }
}

export interface PwaInstallPromptProviderProps {
  children?: React.ReactNode;
  appIconPath?: string;
  /**
   * Whether the prompt may be SHOWN now. Chrome's install offer is captured
   * from mount regardless — it arrives once per page load, often before the
   * account has loaded — and held until this turns true.
   */
  canPresent: boolean;
}
export const PwaInstallPromptProvider = ({
  appIconPath,
  canPresent,
  children,
}: PwaInstallPromptProviderProps) => {
  const { formatMessage } = useIntl();
  const isMobile = useIsMobileShell();

  const [isSheetOpen, setSheetOpen] = React.useState(false);
  const [deferredPrompt, setDeferredPrompt] =
    React.useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setInstalled] = React.useState(isInstalledApp);

  const { userAgent } = navigator;
  const isIos = isIosDevice(userAgent);
  const showsMoreStep = hidesShareBehindMore(userAgent);
  // Install only makes sense on a phone (owner, 04.11.2026).
  const canOfferInstall =
    isMobile && canPresent && !isInstalled && !isInAppBrowser(userAgent);

  React.useEffect(() => {
    if (!isMobile || isIos) return;
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () =>
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt,
      );
  }, [isMobile, isIos]);

  React.useEffect(() => {
    const handleAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => window.removeEventListener('appinstalled', handleAppInstalled);
  }, []);

  // The device's one show is spent when the sheet is SHOWN, not when the
  // offer is captured — an offer held on the sign-in page has not been seen.
  React.useEffect(() => {
    if (!canOfferInstall) return;
    const isFirstShow = isIos
      ? claimPromptShow(
          IOS_PROMPT_SHOWN_KEY,
          showsMoreStep ? undefined : LEGACY_IOS_PROMPT_KEY,
        )
      : Boolean(deferredPrompt) && claimPromptShow(ANDROID_PROMPT_SHOWN_KEY);
    if (isFirstShow) setSheetOpen(true);
  }, [canOfferInstall, deferredPrompt, isIos, showsMoreStep]);

  const promptAndroidInstall = React.useCallback(() => {
    if (!deferredPrompt) return;
    // An offer opens Chrome's dialog once; a second `prompt()` rejects. Spent
    // here, so the menu entry goes until Chrome sends a new offer.
    setDeferredPrompt(null);
    setSheetOpen(false);
    deferredPrompt.prompt().catch(() => undefined);
    deferredPrompt.userChoice.then(
      ({ outcome }) => {
        if (outcome === 'accepted') setInstalled(true);
      },
      () => undefined,
    );
  }, [deferredPrompt]);

  const canInstall = canOfferInstall && (isIos || Boolean(deferredPrompt));
  const openInstall = React.useCallback(() => {
    if (!canInstall) return;
    if (isIos) setSheetOpen(true);
    else promptAndroidInstall();
  }, [canInstall, isIos, promptAndroidInstall]);

  const providedValue = React.useMemo(
    () => ({ canInstall, openInstall }),
    [canInstall, openInstall],
  );

  const iosSteps: PwaInstallStep[] = showsMoreStep
    ? [
        { icon: 'more', label: formatMessage(messages.stepMore) },
        { icon: 'share', label: formatMessage(messages.stepShareInMenu) },
        {
          icon: 'addToHomeScreen',
          label: formatMessage(messages.stepAddToHomeScreen),
        },
      ]
    : [
        { icon: 'share', label: formatMessage(messages.stepShare) },
        {
          icon: 'addToHomeScreen',
          label: formatMessage(messages.stepAddToHomeScreen),
        },
      ];

  return (
    <PwaInstallPromptContext.Provider value={providedValue}>
      {children}
      {isSheetOpen && canInstall && (
        <PwaInstallSheet
          closeLabel={formatMessage(messages.close)}
          description={formatMessage(messages.installDescription)}
          title={formatMessage(messages.installTitle)}
          onClose={() => setSheetOpen(false)}
          appIconPath={appIconPath}
        >
          {isIos ? (
            <PwaInstallSteps steps={iosSteps} />
          ) : (
            // Chrome installs with its own dialog, opened from the offer it
            // sent: one Install button, not the iPhone's steps.
            <Button
              data-testid="pwa-install-prompt-install"
              onClick={promptAndroidInstall}
              variant="contained"
              fullWidth
            >
              {formatMessage(messages.installButton)}
            </Button>
          )}
        </PwaInstallSheet>
      )}
    </PwaInstallPromptContext.Provider>
  );
};

export function usePwaInstallPromptContext() {
  return PwaInstallPromptContext.useContext();
}
